import { motion } from "framer-motion";
import React, { FC } from "react";
import rightImgPng from "images/uae-min.webp";
import NcImage from "shared/NcImage/NcImage";
import Badge from "shared/Badge/Badge";
import { fadeInUp, staggerContainer } from "utils/motionPresets";

export interface SectionOurFeaturesProps {
  className?: string;
  rightImg?: string;
  type?: "type1" | "type2";
}

const features = [
  {
    badge: "Quality",
    badgeColor: undefined as "green" | "red" | undefined,
    title: "Premium UK-Manufactured Building Solutions",
    desc: "Designed and engineered in the UK, our products meet the highest international standards.",
  },
  {
    badge: "Stock",
    badgeColor: "green" as const,
    title: "Largest Stock in Dubai",
    desc: "Extensive inventory of aluminum, stainless steel, PVC, and rubber profiles with fast delivery.",
  },
  {
    badge: "Trust",
    badgeColor: "red" as const,
    title: "Trusted Across the Middle East",
    desc: "Long-lasting, precision-engineered solutions professionals across the UAE rely on.",
  },
];

const SectionOurFeatures: FC<SectionOurFeaturesProps> = ({
  className = "lg:py-10",
  rightImg = rightImgPng,
  type = "type1",
}) => {
  return (
    <div
      className={`nc-SectionOurFeatures relative flex flex-col items-center gap-8 ${
        type === "type1" ? "lg:flex-row lg:items-center" : "lg:flex-row-reverse lg:items-center"
      } ${className}`}
      data-nc-id="SectionOurFeatures"
    >
      {/* Smaller image */}
      <motion.div
        className="w-full max-w-[280px] sm:max-w-xs lg:max-w-sm flex-shrink-0 mx-auto lg:mx-0"
        initial={{ opacity: 0, x: type === "type1" ? -30 : 30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <div className="relative rounded-2xl overflow-hidden shadow-bcr aspect-[4/3]">
          <NcImage src={rightImg} containerClassName="w-full h-full" className="object-cover w-full h-full" />
        </div>
      </motion.div>

      <motion.div
        className={`flex-1 w-full ${type === "type1" ? "lg:pl-8 xl:pl-12" : "lg:pr-8 xl:pr-12"}`}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-60px" }}
        variants={staggerContainer}
      >
        <motion.h2 variants={fadeInUp} className="font-bold text-2xl sm:text-3xl md:text-4xl text-center lg:text-left">
          We Understand <span className="bcr-gradient-text">You!</span>
        </motion.h2>

        <ul className="space-y-4 sm:space-y-5 mt-6">
          {features.map((feature, index) => (
            <motion.li
              key={index}
              variants={fadeInUp}
              className="flex gap-4 p-4 rounded-xl bcr-card-modern"
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 font-bold text-sm">
                {index + 1}
              </div>
              <div>
                <Badge color={feature.badgeColor} name={feature.badge} />
                <h3 className="text-base sm:text-lg font-semibold text-neutral-900 dark:text-neutral-100 mt-1">
                  {feature.title}
                </h3>
                <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed">
                  {feature.desc}
                </p>
              </div>
            </motion.li>
          ))}
        </ul>
      </motion.div>
    </div>
  );
};

export default SectionOurFeatures;
